import { grossFromNet } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Archive,
  ArchiveRestore,
  Boxes,
  ChevronLeft,
  Globe,
  Link2,
  MoreHorizontal,
  Pencil,
  Split,
  Truck,
  Merge,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Menu } from "~/components/ui/menu";
import { Page, PageHeader, Panel, Stat } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { History } from "~/features/audit/history";
import { ProductFormSheet, productQuery, type Product, type ProductVariant } from "~/features/products/product";
import { VariantCompositionSheet } from "~/features/products/variant-composition";
import { VariantSuppliersSheet } from "~/features/products/variant-suppliers";
import { SerialTrackingPanel } from "~/features/products/serial-tracking";
import { SplitVariantsSheet } from "~/features/products/split-variants";
import { AttachVariantSheet, attachableVariants } from "~/features/products/attach-variant";
import { MergeVariantSheet } from "~/features/products/merge-variant";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { helpHint } from "~/features/help/hint";
import { useMarginRate } from "~/lib/margin";

export const Route = createFileRoute("/_app/products/$id")({
  // Not awaited: the page renders its skeleton at once, then data or a retryable error.
  loader: ({ context, params }) => void context.queryClient.prefetchQuery(productQuery(params.id)),
  component: ProductPage,
});

function BackLink() {
  const t = useT();
  return (
    <Link to="/products" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("products.title")}
    </Link>
  );
}

function ProductPage() {
  const { id } = Route.useParams();
  const query = useQuery(productQuery(id));
  if (query.isPending)
    return (
      <Page>
        <BackLink />
        <Skeleton className="h-8 w-96" />
        <Skeleton className="mt-6 h-72 w-full" />
      </Page>
    );
  if (query.isError)
    return (
      <Page>
        <BackLink />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  return <ProductView product={query.data} refreshing={query.isFetching} />;
}

function Section({ title, children, flush }: { title: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <div className="border-b border-line last:border-b-0">
      <h2 className="px-5 pt-5 pb-3 text-[15px] font-medium md:px-6">{title}</h2>
      <div className={flush ? "" : "px-5 pb-5 md:px-6"}>{children}</div>
    </div>
  );
}

function Options({ variant }: { variant: ProductVariant }) {
  if (variant.options.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {variant.options.map((o) => (
        <Badge key={o.name} title={o.name}>
          {o.value}
        </Badge>
      ))}
    </span>
  );
}

/** `refreshing`: the cached product is being reloaded (after a receipt, say); editing waits for it. */
function ProductView({ product, refreshing = false }: { product: Product; refreshing?: boolean }) {
  const [suppliersOf, setSuppliersOf] = useState<{ id: string; sku: string } | null>(null);
  const [compositionOf, setCompositionOf] = useState<{ id: string; sku: string } | null>(null);
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [merging, setMerging] = useState(false);
  // Variants combining two sizes (vest · cummerbund) can become kits of one article per size.
  const splittable =
    !product.serialTracked && (product.variants[0]?.options.length ?? 0) >= 2 && !product.variants.some((v) => v.isKit);
  const archived = Boolean(product.archivedAt);
  const isWeb = Boolean(product.shopifyProductId);
  const ttc = (cents: number) => format.money(grossFromNet(cents, product.vatRateBp));
  // The warehouse does not see costs: the API leaves them out, the columns too.
  const seesCosts = can("costs:read");
  // A product without options (one size, imported apart) does not spend a column on them.
  const hasOptions = product.variants.some((v) => v.options.length > 0);

  const toggleArchive = useMutation({
    mutationFn: () =>
      unwrap(archived ? api.products({ id: product.id }).restore.post() : api.products({ id: product.id }).archive.post()),
    onSuccess: (saved) => {
      queryClient.setQueryData(["products", product.id], saved);
      void queryClient.invalidateQueries({ queryKey: ["products", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["audit", "product", product.id] });
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  return (
    <Page>
      <PageHeader
        back={<BackLink />}
        title={<span className="whitespace-normal">{product.title}</span>}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            {isWeb ? (
              <Badge tone="info">
                <Globe className="size-3" aria-hidden />
                {t("products.channels.web")}
              </Badge>
            ) : (
              <Badge>{t("products.channels.offline")}</Badge>
            )}
            {product.productType ? <span>{product.productType}</span> : null}
            {archived ? <Badge tone="warning">{t("customers.showArchived")}</Badge> : null}
          </span>
        }
        actions={
          can("products:write") ? (
            <>
              <Button onClick={() => setEditing(true)} disabled={refreshing}>
                <Pencil />
                {t("common.actions.edit")}
              </Button>
              <Menu
                trigger={
                  <Button variant="ghost" size="icon" aria-label={t("common.actions.more")}>
                    <MoreHorizontal />
                  </Button>
                }
                actions={[
                  // Splitting moves the stock on hand to the new items: it takes the stock right too.
                  ...(splittable && can("stock:write")
                    ? [
                        {
                          label: t("products.split.title"),
                          hint: helpHint(t, "product.split"),
                          icon: <Split />,
                          onSelect: () => setSplitting(true),
                        },
                      ]
                    : []),
                  // A variant imported apart (IABAKO) joins its product; a web variant stays with Shopify's.
                  ...(!archived && attachableVariants(product).length
                    ? [
                        {
                          label: t("products.attach.title"),
                          icon: <Link2 />,
                          onSelect: () => setAttaching(true),
                        },
                        {
                          label: t("products.merge.title"),
                          icon: <Merge />,
                          onSelect: () => setMerging(true),
                        },
                      ]
                    : []),
                  {
                    label: archived ? t("common.actions.restore") : t("common.actions.archive"),
                    hint: archived ? undefined : helpHint(t, "product.archive"),
                    icon: archived ? <ArchiveRestore /> : <Archive />,
                    onSelect: () => toggleArchive.mutate(),
                  },
                ]}
              />
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 2xl:grid-cols-[minmax(0,1fr)_320px]">
        <Panel>
          <Section title={t("products.variantsTitle", { count: product.variants.length })} flush>
            <div className="overflow-x-auto max-md:hidden">
              <table className="w-full text-sm [&_td]:px-3 [&_th]:px-3 [&_th]:whitespace-nowrap">
                <thead>
                  <tr className="border-y border-line text-[12.5px] text-fg-3">
                    <th className="h-10 pl-6! text-left font-medium">{t("products.fields.sku")}</th>
                    {hasOptions ? <th className="text-left font-medium">{t("products.fields.options")}</th> : null}
                    <th className="text-right font-medium">{t("products.fields.salePrice")}</th>
                    {seesCosts ? (
                      <>
                        <th className="text-right font-medium" title={t("products.fields.purchasePriceHint")}>
                          {t("products.fields.purchasePrice")}
                        </th>
                        <th className="text-right font-medium">{t("products.fields.margin")}</th>
                      </>
                    ) : null}
                    <th className="text-right font-medium max-2xl:hidden">{t("products.fields.weightGrams")}</th>
                    <th className="text-right font-medium">{t("products.fields.reorderPoint")}</th>
                    <th className="text-right font-medium max-xl:hidden">{t("products.fields.targetStock")}</th>
                    <th className="text-left font-medium max-lg:hidden">{t("products.fields.mainSupplier")}</th>
                    <th className="w-24 pr-4!" aria-label={t("products.suppliers.title")} />
                  </tr>
                </thead>
                <tbody>
                  {product.variants.map((v) => (
                    <tr key={v.id} className="border-b border-line last:border-b-0">
                      <td className="h-14 pl-6! font-medium tabular whitespace-nowrap">
                        <span className="flex items-center gap-2">
                          {v.sku}
                          {v.isKit ? <Badge tone="gold">{t("products.composition.title")}</Badge> : null}
                        </span>
                      </td>
                      {hasOptions ? (
                        <td className="py-2">
                          <Options variant={v} />
                        </td>
                      ) : null}
                      <td className="text-right tabular whitespace-nowrap">
                        <span className="block">{format.money(v.salePriceCents)}</span>
                        <span className="block text-xs text-fg-3">
                          {t("products.priceTtc", { amount: ttc(v.salePriceCents) })}
                        </span>
                      </td>
                      {seesCosts ? <CostCells salePriceCents={v.salePriceCents} costCents={v.costCents} /> : null}
                      <td className="text-right text-fg-2 tabular max-2xl:hidden">
                        {v.weightGrams != null ? format.number(v.weightGrams) : ""}
                      </td>
                      <td className="text-right tabular">{format.number(v.reorderPoint)}</td>
                      <td className="text-right tabular max-xl:hidden">{format.number(v.targetStock)}</td>
                      <td className="max-w-56 truncate text-fg-2 max-lg:hidden" title={v.mainSupplierName ?? undefined}>
                        {v.mainSupplierName ?? ""}
                      </td>
                      <td className="pr-4! text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("products.composition.open", { sku: v.sku })}
                          onClick={() => setCompositionOf({ id: v.id, sku: v.sku })}
                        >
                          <Boxes />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("products.suppliers.open", { sku: v.sku })}
                          onClick={() => setSuppliersOf({ id: v.id, sku: v.sku })}
                        >
                          <Truck />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="divide-y divide-line border-t border-line md:hidden">
              {product.variants.map((v) => (
                <li key={v.id} className="flex items-start justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 font-medium tabular">
                      {v.sku}
                      {v.isKit ? <Badge tone="gold">{t("products.composition.title")}</Badge> : null}
                    </p>
                    <div className="mt-1">
                      <Options variant={v} />
                    </div>
                  </div>
                  <div className="flex shrink-0 items-start gap-1">
                    <div className="text-right tabular">
                      <p>{format.money(v.salePriceCents)}</p>
                      <p className="text-xs text-fg-3">{t("products.priceTtc", { amount: ttc(v.salePriceCents) })}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("products.composition.open", { sku: v.sku })}
                      onClick={() => setCompositionOf({ id: v.id, sku: v.sku })}
                    >
                      <Boxes />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("products.suppliers.open", { sku: v.sku })}
                      onClick={() => setSuppliersOf({ id: v.id, sku: v.sku })}
                    >
                      <Truck />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </Section>
          {product.description ? (
            <Section title={t("products.fields.description")}>
              <p className="line-clamp-[12] text-sm whitespace-pre-line text-fg-2">{product.description}</p>
            </Section>
          ) : null}
        </Panel>

        <aside className="flex flex-col gap-5">
          <Panel>
            <div className="px-5 py-5">
              <dl className="grid grid-cols-2 gap-4">
                <Stat label={t("products.fields.productType")} value={product.productType ?? "—"} />
                <Stat label={t("products.fields.vatRate")} value={format.percent(product.vatRateBp / 10_000)} />
              </dl>
            </div>
          </Panel>
          <SerialTrackingPanel product={product} />
          <Panel>
            <Section title={t("audit.history")}>
              <History entityType="product" entityId={product.id} />
            </Section>
          </Panel>
        </aside>
      </div>

      <ProductFormSheet
        open={editing}
        onOpenChange={setEditing}
        product={product}
        onSaved={(saved) => {
          queryClient.setQueryData(["products", product.id], saved);
          void queryClient.invalidateQueries({ queryKey: ["audit", "product", product.id] });
        }}
      />
      <VariantSuppliersSheet
        variant={suppliersOf}
        editable={can("products:write")}
        onOpenChange={(open) => (open ? undefined : setSuppliersOf(null))}
      />
      <SplitVariantsSheet product={product} open={splitting} onOpenChange={setSplitting} />
      <AttachVariantSheet product={product} open={attaching} onOpenChange={setAttaching} />
      <MergeVariantSheet product={product} open={merging} onOpenChange={setMerging} />
      <VariantCompositionSheet
        variant={compositionOf}
        editable={can("products:write")}
        onOpenChange={(open) => (open ? undefined : setCompositionOf(null))}
      />
    </Page>
  );
}

/** Purchase price (weighted average cost) and the margin it leaves on the sale price, both excl. VAT. */
function CostCells({ salePriceCents, costCents }: { salePriceCents: number; costCents?: number | null }) {
  const t = useT();
  const format = useFormat();
  const margin = useMarginRate();
  if (!costCents) {
    return (
      <td colSpan={2} className="text-right text-fg-3 whitespace-nowrap">
        {t("products.fields.costMissing")}
      </td>
    );
  }
  const ratio = margin.rate(salePriceCents - costCents, costCents);
  const negative = salePriceCents < costCents;
  return (
    <>
      <td className="text-right tabular whitespace-nowrap">{format.money(costCents)}</td>
      <td className={negative ? "text-right tabular whitespace-nowrap text-danger" : "text-right tabular whitespace-nowrap"}>
        <span className="block">{format.money(salePriceCents - costCents)}</span>
        {ratio !== null ? <span className="block text-xs text-fg-3">{format.percent(ratio)}</span> : null}
      </td>
    </>
  );
}
